import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';

const planSchema = z.object({
  name: z.string().min(1, 'Nome e obrigatorio'),
  slug: z.string().min(1, 'Slug e obrigatorio'),
  type: z.enum(['basic', 'pro', 'enterprise']),
  currency: z.enum(['BRL', 'GBP', 'USD']),
  priceMonthly: z.string().min(1, 'Preco e obrigatorio'),
  priceYearly: z.string().optional(),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  isActive: z.boolean(),
  displayOrder: z.number(),
  stripePriceIdMonthly: z.string().optional(),
  stripePriceIdYearly: z.string().optional(),
  limits: z.object({
    photosPerMonth: z.number().nullable(),
    audiosPerMonth: z.number().nullable(),
    textsPerMonth: z.number().nullable(),
    storageMaxMb: z.number().nullable(),
    receiptRetentionDays: z.number(),
    canExportPdf: z.boolean(),
    canExportCsv: z.boolean(),
    hasAnnualReport: z.boolean(),
    hasPrioritySupport: z.boolean(),
    hasOpenBanking: z.boolean(),
  }),
});

type PlanFormData = z.infer<typeof planSchema>;

interface PlanFormProps {
  defaultValues?: Partial<PlanFormData>;
  onSubmit: (data: PlanFormData) => void;
  isLoading?: boolean;
}

const formDefaults: PlanFormData = {
  name: '',
  slug: '',
  type: 'basic',
  currency: 'BRL',
  priceMonthly: '',
  priceYearly: '',
  description: '',
  features: [],
  isActive: true,
  displayOrder: 0,
  stripePriceIdMonthly: '',
  stripePriceIdYearly: '',
  limits: {
    photosPerMonth: 30,
    audiosPerMonth: 20,
    textsPerMonth: null,
    storageMaxMb: 500,
    receiptRetentionDays: 365,
    canExportPdf: false,
    canExportCsv: true,
    hasAnnualReport: false,
    hasPrioritySupport: false,
    hasOpenBanking: false,
  },
};

export const PlanForm: React.FC<PlanFormProps> = ({ defaultValues, onSubmit, isLoading }) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<PlanFormData>({
    resolver: zodResolver(planSchema),
    defaultValues: formDefaults,
  });

  // Reset form when defaultValues change (e.g., when plan data is loaded)
  useEffect(() => {
    if (defaultValues) {
      reset({ ...formDefaults, ...defaultValues });
    }
  }, [defaultValues, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informacoes Basicas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome do Plano"
              {...register('name')}
              error={errors.name?.message}
            />
            <Input
              label="Slug"
              {...register('slug')}
              placeholder="basic-br"
              error={errors.slug?.message}
            />
            <Select
              label="Tipo"
              {...register('type')}
              options={[
                { value: 'basic', label: 'Basic' },
                { value: 'pro', label: 'Pro' },
                { value: 'enterprise', label: 'Enterprise' },
              ]}
            />
            <Select
              label="Moeda"
              {...register('currency')}
              options={[
                { value: 'BRL', label: 'BRL (R$)' },
                { value: 'GBP', label: 'GBP (£)' },
                { value: 'USD', label: 'USD ($)' },
              ]}
            />
            <Input
              label="Preco Mensal"
              type="number"
              step="0.01"
              {...register('priceMonthly')}
              error={errors.priceMonthly?.message}
            />
            <Input
              label="Preco Anual"
              type="number"
              step="0.01"
              {...register('priceYearly')}
            />
          </div>
          <div className="mt-4">
            <Textarea
              label="Descricao"
              {...register('description')}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Limites</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Controller
              name="limits.photosPerMonth"
              control={control}
              render={({ field }) => (
                <Input
                  label="Fotos/mes"
                  type="number"
                  placeholder="Ilimitado se vazio"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                />
              )}
            />
            <Controller
              name="limits.audiosPerMonth"
              control={control}
              render={({ field }) => (
                <Input
                  label="Audios/mes"
                  type="number"
                  placeholder="Ilimitado se vazio"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                />
              )}
            />
            <Controller
              name="limits.textsPerMonth"
              control={control}
              render={({ field }) => (
                <Input
                  label="Textos/mes"
                  type="number"
                  placeholder="Ilimitado se vazio"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                />
              )}
            />
            <Controller
              name="limits.storageMaxMb"
              control={control}
              render={({ field }) => (
                <Input
                  label="Storage Max (MB)"
                  type="number"
                  placeholder="Ilimitado se vazio"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                />
              )}
            />
            <Input
              label="Retencao (dias)"
              type="number"
              {...register('limits.receiptRetentionDays', { valueAsNumber: true })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="limits.canExportCsv"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Export CSV"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="limits.canExportPdf"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Export PDF"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="limits.hasAnnualReport"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Relatorio Anual"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="limits.hasPrioritySupport"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Suporte Prioritario"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="limits.hasOpenBanking"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Open Banking"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Plano Ativo"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Stripe */}
      <Card>
        <CardHeader>
          <CardTitle>Stripe</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Price ID Mensal"
              {...register('stripePriceIdMonthly')}
              placeholder="price_xxx"
            />
            <Input
              label="Price ID Anual"
              {...register('stripePriceIdYearly')}
              placeholder="price_xxx"
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button type="submit" isLoading={isLoading}>
          Salvar Plano
        </Button>
      </div>
    </form>
  );
};

export default PlanForm;
